import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Favorite as FavoriteIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ProductCard from '../Product/ProductCard';
import { useAuthStore } from '../../store/authStore';
import { FavoritesService } from '../../services/favoritesService';
import { ProductService } from '../../services/productService';
import toast from 'react-hot-toast';

export interface FavoritesDrawerProps {
  open: boolean;
  onClose: () => void;
}

type FavoriteItem = {
  id: string;
  title: string;
  subtitle?: string;
  isAuction: boolean;
  product: any;
  thumb?: string;
};

const PAGE_SIZE = 10;

const FavoritesDrawer: React.FC<FavoritesDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();

  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<FavoriteItem[]>([]);
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [allFavoriteIds, setAllFavoriteIds] = React.useState<string[]>([]);
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  const goTo = (path: string) => {
    onClose();
    navigate(path);
  };

  const buildItems = React.useCallback(async () => {
    if (!open) return;
    if (!isAuthenticated || !user?.id) {
      setItems([]);
      setAllFavoriteIds([]);
      setVisibleCount(PAGE_SIZE);
      return;
    }

    setLoading(true);
    try {
      const favs = await FavoritesService.getUserFavorites(user.id);
      if (!Array.isArray(favs) || favs.length === 0) {
        setItems([]);
        setAllFavoriteIds([]);
        setVisibleCount(PAGE_SIZE);
        return;
      }

      const favIds = favs.map((f) => String(f.product_id)).filter(Boolean);
      setAllFavoriteIds(favIds);
      setVisibleCount(PAGE_SIZE);
      setItems([]);
    } catch (e: any) {
      console.error('[FavoritesDrawer] failed to load favorites', e);
      toast.error(e?.message || 'Failed to load favorites');
      setItems([]);
      setAllFavoriteIds([]);
      setVisibleCount(PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  }, [open, isAuthenticated, user?.id]);

  const loadMore = React.useCallback(async () => {
    if (!open) return;
    if (!isAuthenticated || !user?.id) return;
    if (loading || loadingMore) return;
    if (allFavoriteIds.length === 0) return;

    const nextCount = items.length === 0
      ? Math.min(allFavoriteIds.length, PAGE_SIZE)
      : Math.min(allFavoriteIds.length, visibleCount + PAGE_SIZE);
    if (nextCount <= visibleCount && items.length > 0) return;

    setLoadingMore(true);
    try {
      const idsToShow = allFavoriteIds.slice(0, nextCount);
      const already = new Set(items.map((x) => x.id));
      const missing = idsToShow.filter((id) => !already.has(id));
      if (missing.length === 0) {
        setVisibleCount(nextCount);
        return;
      }

      const prods = await ProductService.getProductsWithSellersByIds(missing);
      const byId = new Map<string, any>();
      (prods || []).forEach((p: any) => byId.set(String(p.id), p));

      const mapped: FavoriteItem[] = missing
        .map((id) => {
          const p: any = byId.get(String(id));
          if (!p) return null;
          const isAuction = Boolean(p.auctionId || p.auction);
          const title = String(p.name || 'Untitled');
          const subtitle = isAuction ? 'Auktion' : ((p as any).productType === 'p' ? 'Angebot' : 'Produkt');
          const thumb = String((p as any).image || (Array.isArray((p as any).images) ? (p as any).images[0] : '') || '').trim();
          return {
            id: String(p.id),
            title,
            subtitle,
            isAuction,
            product: p,
            thumb: thumb || undefined,
          } as FavoriteItem;
        })
        .filter(Boolean) as FavoriteItem[];

      setItems((prev) => {
        const next = [...prev, ...mapped];
        const order = new Map<string, number>();
        allFavoriteIds.slice(0, nextCount).forEach((id, idx) => order.set(id, idx));
        next.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
        return next;
      });
      setVisibleCount(nextCount);
    } catch (e: any) {
      console.error('[FavoritesDrawer] loadMore failed', e);
      toast.error(e?.message || 'Failed to load more favorites');
    } finally {
      setLoadingMore(false);
    }
  }, [open, isAuthenticated, user?.id, loading, loadingMore, allFavoriteIds, visibleCount, items]);

  React.useEffect(() => {
    if (!open) {
      setExpanded(false);
      setVisibleCount(PAGE_SIZE);
      return;
    }
    void buildItems();
  }, [open, buildItems]);

  React.useEffect(() => {
    if (!open) return;
    // When favorites IDs are loaded/reset, load first page.
    if (allFavoriteIds.length > 0) {
      void loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allFavoriteIds]);

  React.useEffect(() => {
    if (!open) return;
    const el = loadMoreRef.current;
    if (!el) return;
    if (visibleCount >= allFavoriteIds.length) return;
    const root = scrollRef.current;

    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (hit) void loadMore();
      },
      { root: root || null, rootMargin: '220px' }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [open, visibleCount, allFavoriteIds.length, loadMore]);

  React.useEffect(() => {
    const onFavoriteChanged = () => {
      if (!open) return;
      void buildItems();
    };
    window.addEventListener('favorite-changed', onFavoriteChanged as EventListener);
    return () => window.removeEventListener('favorite-changed', onFavoriteChanged as EventListener);
  }, [open, buildItems]);

  const handleRemoveFavorite = async (productId: string) => {
    if (!user?.id) return;
    try {
      await FavoritesService.removeFromFavorites(productId, user.id);
      setItems((prev) => prev.filter((x) => x.id !== productId));
      setAllFavoriteIds((prev) => prev.filter((id) => id !== productId));
      window.dispatchEvent(new CustomEvent('favorite-changed', { detail: { id: productId, isFavorited: false, isJob: false } }));
      toast.success('Removed from favorites');
      if (expanded === productId) setExpanded(false);
    } catch (e: any) {
      console.error('[FavoritesDrawer] remove failed', e);
      toast.error(e?.message || 'Failed to remove favorite');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 460 },
          maxWidth: '100vw',
          borderTopLeftRadius: { xs: 0, sm: 20 },
          borderBottomLeftRadius: { xs: 0, sm: 20 },
          overflow: 'hidden',
          boxShadow: '0 30px 90px rgba(17,24,39,0.22)',
          borderLeft: '1px solid rgba(0,0,0,0.06)',
          background:
            'radial-gradient(1000px 420px at 10% 0%, rgba(99,102,241,0.10), transparent), radial-gradient(900px 420px at 100% 10%, rgba(14,165,233,0.10), transparent), linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={(t) => ({
            px: 2.25,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            backgroundColor: 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${t.palette.divider}`,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={(t) => ({
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: t.palette.error.main,
                backgroundColor: 'rgba(239,68,68,0.10)',
                border: `1px solid rgba(239,68,68,0.25)`,
              })}
            >
              <FavoriteIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }}>Favorites</Typography>
              <Typography variant="caption" color="text.secondary">
                {items.length} item{items.length === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={() => void buildItems()}
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.08)',
                background: 'rgba(255,255,255,0.7)',
                '&:hover': { background: 'rgba(255,255,255,0.9)' },
              }}
              aria-label="Refresh favorites"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>

            <IconButton
              onClick={onClose}
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.08)',
                background: 'rgba(255,255,255,0.7)',
                '&:hover': { background: 'rgba(255,255,255,0.9)' },
              }}
              aria-label="Close favorites"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ px: 2.25, py: 1.75 }}>
          <Button
            onClick={() => goTo('/dashboard?tab=favorites')}
            fullWidth
            variant="contained"
            startIcon={<OpenInNewIcon />}
            sx={{
              borderRadius: 3,
              py: 1.15,
              fontWeight: 900,
              background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
              boxShadow: (t) => `0 16px 34px ${t.palette.primary.main}2A`,
              '&:hover': {
                boxShadow: (t) => `0 18px 44px ${t.palette.primary.main}38`,
              },
            }}
          >
            Open favorites in dashboard
          </Button>
        </Box>

        <Divider />

        <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', px: 2.25, py: 2 }}>
          {!isAuthenticated ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Box
                sx={{
                  mx: 'auto',
                  width: 84,
                  height: 84,
                  borderRadius: 999,
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  color: 'error.main',
                  mb: 2,
                }}
              >
                <FavoriteIcon sx={{ fontSize: 34 }} />
              </Box>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Sign in to use favorites</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.25 }}>
                Your saved items will appear here.
              </Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Box
                sx={{
                  mx: 'auto',
                  width: 84,
                  height: 84,
                  borderRadius: 999,
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  color: 'error.main',
                  mb: 2,
                }}
              >
                <FavoriteIcon sx={{ fontSize: 34 }} />
              </Box>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>No favorites yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.25 }}>
                Tap the heart on any product or auction to save it.
              </Typography>
              <Button variant="contained" onClick={() => goTo('/all-products')} sx={{ borderRadius: 3, fontWeight: 900 }}>
                Browse products
              </Button>
            </Box>
          ) : (
            <Stack spacing={1.1}>
              {items.map((item) => (
                <Accordion
                  key={item.id}
                  expanded={expanded === item.id}
                  onChange={(_, isExp) => setExpanded(isExp ? item.id : false)}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.06)',
                    bgcolor: 'rgba(255,255,255,0.72)',
                    backdropFilter: 'blur(10px)',
                    '&::before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 1.5,
                      '& .MuiAccordionSummary-content': { my: 1.25 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Avatar
                          variant="rounded"
                          src={item.thumb}
                          alt={item.title}
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: 'rgba(2,132,199,0.10)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            flexShrink: 0,
                          }}
                        />

                        <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap title={item.title}>
                          {item.title}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                          {item.subtitle ? <Chip size="small" label={item.subtitle} sx={{ fontWeight: 800 }} /> : null}
                          {item.isAuction ? <Chip size="small" label="Live bid" sx={{ fontWeight: 800, bgcolor: 'rgba(124,58,237,0.10)' }} /> : null}
                        </Stack>
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFavorite(item.id);
                          }}
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 2,
                            border: '1px solid rgba(239,68,68,0.25)',
                            bgcolor: 'rgba(239,68,68,0.08)',
                            color: 'rgba(185,28,28,1)',
                            '&:hover': { bgcolor: 'rgba(239,68,68,0.12)' },
                          }}
                          aria-label="Remove from favorites"
                        >
                          <Badge variant="dot" color="error" invisible={!item.isAuction}>
                            <FavoriteIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </Stack>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 1.25, pb: 1.5 }}>
                    <ProductCard
                      product={item.product}
                      isFavorite
                      showSeller={false}
                      variant="default"
                      onViewDetails={(id) => goTo(`/product/${id}`)}
                      onToggleFavorite={(id) => handleRemoveFavorite(id)}
                      onShare={() => {}}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}

              <Box ref={loadMoreRef} sx={{ height: 1 }} />

              {loadingMore ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : null}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default FavoritesDrawer;
